import React from 'react';
import { MapPin, Clock, AlertCircle, Truck, CheckCircle, Phone, Mail, Navigation } from 'lucide-react';

const ShippingPolicy = () => {
  const deliveryRules = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Exact Location Required',
      description: 'Customers must select an exact delivery location at the time of booking.',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Specified Location Only',
      description: 'Bikes will only be delivered to the specified location mentioned in the booking.',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Timely Delivery',
      description: 'Our delivery personnel will reach the designated location at the scheduled time.',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      title: 'Customer Presence',
      description: 'Customers must be present at the delivery location to receive the bike.',
      color: 'bg-orange-100 text-orange-600'
    },
    {
      icon: <Navigation className="w-6 h-6" />,
      title: 'Service Area',
      description: 'Delivery service is available only within Pune and Ratnagiri city limits.',
      color: 'bg-indigo-100 text-indigo-600'
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      title: 'No Location Changes',
      description: 'Bikes will not be delivered to unspecified or alternative locations after booking confirmation.',
      color: 'bg-red-100 text-red-600'
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Safe Location',
      description: 'Ensure the delivery location is accessible and safe for our personnel.',
      color: 'bg-teal-100 text-teal-600'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Punctuality Matters',
      description: 'Any delays caused by the customer at the delivery point may incur additional charges.',
      color: 'bg-yellow-100 text-yellow-600'
    }
  ];

  const deliverySteps = [
    {
      step: '1',
      title: 'Book & Select Location',
      description: 'Choose your exact delivery location during the booking process with complete address details.',
      icon: '📍'
    },
    {
      step: '2',
      title: 'Confirmation',
      description: 'Receive booking confirmation with scheduled delivery time and delivery personnel contact details.',
      icon: '✅'
    },
    {
      step: '3',
      title: 'Preparation',
      description: 'Our team prepares your bike, conducts safety checks, and dispatches it at the scheduled time.',
      icon: '🔧'
    },
    {
      step: '4',
      title: 'Delivery',
      description: 'Delivery personnel arrives at your location. You must be present to receive and inspect the bike.',
      icon: '🏍️'
    }
  ];

  const serviceAreas = [
    {
      city: 'Pune',
      coverage: 'Complete City Coverage',
      zones: ['Chakan', 'Hinjewadi', 'Kharadi', 'Hadapsar', 'Baner', 'Wakad', 'Pimpri-Chinchwad'],
      icon: '🏙️'
    },
    {
      city: 'Ratnagiri',
      coverage: 'Complete City Coverage',
      zones: ['City Center', 'Rajapur', 'Lanja', 'Mirjole', 'Pawas', 'Mandangad'],
      icon: '🌊'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center mb-6">
            <Truck className="w-12 h-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">
              Shipping Policy
            </h1>
          </div>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Convenient doorstep delivery service for your two-wheeler rental
          </p>
        </div>
      </div>

      {/* Important Notice */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-600 rounded-lg p-6 shadow-md">
          <div className="flex items-start space-x-4">
            <Truck className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Hassle-Free Bike Delivery
              </h3>
              <p className="text-gray-700 leading-relaxed">
                VE GO Rental Service offers convenient doorstep delivery within Pune and Ratnagiri. Simply specify your location during booking, and we'll bring your chosen two-wheeler right to your doorstep at your scheduled time. Please read our delivery terms below to ensure a smooth experience.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Process */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
          How Delivery Works
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          Our streamlined delivery process ensures your bike reaches you safely and on time
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {deliverySteps.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
            >
              <div className="text-5xl mb-4 text-center">{item.icon}</div>
              <div className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center mb-4 mx-auto font-bold text-lg">
                {item.step}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
                {item.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed text-center">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Delivery Rules */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Delivery Terms & Conditions
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Please review these important delivery guidelines to ensure smooth service
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {deliveryRules.map((rule, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`${rule.color} w-14 h-14 rounded-full flex items-center justify-center mb-4`}>
                  {rule.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {rule.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {rule.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Service Areas */}
      {/* <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
          Service Coverage Areas
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          We currently offer delivery services in the following locations
        </p>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {serviceAreas.map((area, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-lg p-8 border-2 border-blue-100 hover:border-blue-300 transition-colors duration-300"
            >
              <div className="flex items-center mb-6">
                <span className="text-5xl mr-4">{area.icon}</span>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{area.city}</h3>
                  <p className="text-sm text-green-600 font-medium">{area.coverage}</p>
                </div>
              </div>
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Coverage Zones:</h4>
                <div className="flex flex-wrap gap-2">
                  {area.zones.map((zone, idx) => (
                    <span
                      key={idx}
                      className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      {zone}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div> */}

      {/* Important Guidelines */}
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Important Delivery Guidelines
          </h2>
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
            <div className="space-y-6">
              <div className="flex items-start space-x-4 pb-6 border-b border-gray-200">
                <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                  <MapPin className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Provide Complete Address</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Include building name/number, street, landmark, and clear directions. This helps our delivery team locate you quickly and ensures on-time delivery.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 pb-6 border-b border-gray-200">
                <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
                  <Phone className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Stay Reachable</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Keep your phone accessible during delivery time. Our team will call you upon arrival. Unavailability may result in delivery failure and additional charges.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 pb-6 border-b border-gray-200">
                <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Inspect Before Accepting</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Thoroughly inspect the bike for any damages or issues before signing acceptance. Report any concerns immediately to our delivery personnel.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 pb-6 border-b border-gray-200">
                <div className="bg-yellow-100 p-2 rounded-lg flex-shrink-0">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Be Punctual</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Be ready at the delivery location at the scheduled time. Delays exceeding 15 minutes may result in a rescheduling fee of ₹200 or cancellation.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-red-100 p-2 rounded-lg flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Accessible Location</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Ensure the delivery address is accessible by two-wheeler. If the location is inside a gated community or requires special access, inform us during booking.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Charges */}
      {/* <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
          Delivery Charges
        </h2>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-md p-6 text-center border-2 border-gray-200 hover:border-blue-500 transition-colors duration-300">
            <div className="text-3xl mb-3">🏢</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Within 5 km</h3>
            <div className="text-3xl font-bold text-blue-600 mb-2">Free</div>
            <p className="text-sm text-gray-600">No delivery charges for nearby locations</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 text-center border-2 border-blue-500 hover:border-blue-600 transition-colors duration-300 transform scale-105">
            <div className="bg-blue-100 text-xs font-semibold text-blue-600 px-3 py-1 rounded-full inline-block mb-3">
              POPULAR
            </div>
            <div className="text-3xl mb-3">🏙️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">5-15 km</h3>
            <div className="text-3xl font-bold text-blue-600 mb-2">₹100</div>
            <p className="text-sm text-gray-600">Standard delivery charge</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 text-center border-2 border-gray-200 hover:border-blue-500 transition-colors duration-300">
            <div className="text-3xl mb-3">🛣️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">15+ km</h3>
            <div className="text-3xl font-bold text-blue-600 mb-2">₹200</div>
            <p className="text-sm text-gray-600">Extended distance delivery</p>
          </div>
        </div>
        <p className="text-center text-sm text-gray-500 mt-8">
          * Delivery charges are calculated from our office location to your delivery address
        </p>
      </div> */}

      {/* FAQ Section */}
      <div className="bg-gray-50 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Delivery FAQs
          </h2>
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">Can I change the delivery location after booking?</h3>
              <p className="text-gray-600 text-sm">
                No, delivery location changes are not permitted after booking confirmation. Please ensure you provide the correct address during booking.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">What if I'm not available at the delivery time?</h3>
              <p className="text-gray-600 text-sm">
                If you're unavailable, contact us immediately at 9921426002 to reschedule. Rescheduling within 2 hours of delivery may incur additional charges.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">Do you deliver outside Pune and Ratnagiri?</h3>
              <p className="text-gray-600 text-sm">
                Currently, we only offer delivery services within Pune and Ratnagiri city limits. For other locations, you can pick up from our office.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">How will I know when the bike is arriving?</h3>
              <p className="text-gray-600 text-sm">
                Our delivery personnel will call you 15-20 minutes before arrival. You'll also receive their contact number for real-time coordination.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl shadow-2xl p-8 md:p-12 text-white">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Questions About Delivery?</h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              Our team is ready to assist you with any delivery-related queries or special requests
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <a
              href="tel:9921426002"
              className="bg-white text-blue-600 rounded-lg p-6 hover:bg-blue-50 transition-colors duration-200 flex items-center space-x-4 shadow-lg"
            >
              <div className="bg-blue-100 p-3 rounded-full">
                <Phone className="w-6 h-6" />
              </div>
              <div className="text-left">
                <div className="text-sm text-gray-600 font-medium">Call Us</div>
                <div className="text-lg font-semibold">9921426002</div>
              </div>
            </a>
            <a
              href="mailto:vegobike00@gmail.com"
              className="bg-white text-blue-600 rounded-lg p-6 hover:bg-blue-50 transition-colors duration-200 flex items-center space-x-4 shadow-lg"
            >
              <div className="bg-blue-100 p-3 rounded-full">
                <Mail className="w-6 h-6" />
              </div>
              <div className="text-left">
                <div className="text-sm text-gray-600 font-medium">Email Us</div>
                <div className="text-lg font-semibold break-all">vegobike00@gmail.com</div>
              </div>
            </a>
          </div>
          <div className="text-center mt-8">
            <p className="text-sm text-blue-200">
              Delivery Support: Monday - Sunday, 9:00 AM - 8:00 PM IST
            </p>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center text-sm text-gray-500">
          <p className="mb-2">Last Updated: October 2025</p>
          <p>
            VE GO Rental Service reserves the right to modify this delivery policy. Please check our website for the latest updates.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;
